// @author Jayden Scahill - Old Mate Developments

import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthLayout } from './layouts/AuthLayout';
import { DashboardLayout } from './layouts/DashboardLayout';
import { useAuthStore } from './store/authStore';
import { ToastProvider } from './components/ui/Toast';

// Lazy load pages for better performance
const Login = lazy(() => import('./pages/auth/Login').then(m => ({ default: m.Login })));
const Register = lazy(() => import('./pages/auth/Register').then(m => ({ default: m.Register })));
const Dashboard = lazy(() => import('./pages/Dashboard').then(m => ({ default: m.Dashboard })));
const Customers = lazy(() => import('./pages/Customers').then(m => ({ default: m.Customers })));
const CustomerDetails = lazy(() => import('./pages/CustomerDetails').then(m => ({ default: m.CustomerDetails })));
const Tickets = lazy(() => import('./pages/Tickets').then(m => ({ default: m.Tickets })));
const TicketDetails = lazy(() => import('./components/tickets/TicketDetails').then(m => ({ default: m.TicketDetails })));
const NewTicketPage = lazy(() => import('./pages/NewTicketPage').then(m => ({ default: m.NewTicketPage })));
const Invoices = lazy(() => import('./pages/Invoices').then(m => ({ default: m.Invoices })));
const InvoiceDetails = lazy(() => import('./pages/InvoiceDetails').then(m => ({ default: m.InvoiceDetails })));
const Settings = lazy(() => import('./pages/Settings').then(m => ({ default: m.Settings })));

// Loading fallback component
const LoadingFallback = () => (
  <div className="min-h-screen bg-dark-900 flex items-center justify-center">
    <div className="bg-dark-800 rounded-lg p-8 flex flex-col items-center space-y-4">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      <p className="text-white text-lg">Loading...</p>
    </div>
  </div>
);

function App() {
  const { user } = useAuthStore();

  return (
    <Router>
      <ToastProvider>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            <Route element={<AuthLayout />}>
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
            </Route>
            
            <Route element={<DashboardLayout />}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/customers/:id" element={<CustomerDetails />} />
              <Route path="/tickets" element={<Tickets />} />
              <Route path="/tickets/new" element={<NewTicketPage />} />
              <Route path="/tickets/:id" element={<TicketDetails />} />
              <Route path="/invoices" element={<Invoices />} />
              <Route path="/invoices/:id" element={<InvoiceDetails />} />
              <Route path="/settings" element={<Settings />} />
            </Route>

            {/* Catch all route */}
            <Route path="*" element={
              <Navigate to={user ? "/" : "/login"} replace />
            } />
          </Routes>
        </Suspense>
      </ToastProvider>
    </Router>
  );
}

export default App;